import React, { useEffect, useMemo, useRef, useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Slider } from "../../../components/ui/slider";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

const COUNTRIES = [
  "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", "Brazil",
  "Canada", "China", "Denmark", "Egypt", "Finland", "France", "Germany",
  "Greece", "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan",
  "Kenya", "Malaysia", "Mexico", "Netherlands", "New Zealand", "Nigeria",
  "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal",
  "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea",
  "Spain", "Sri Lanka", "Sweden", "Switzerland", "Thailand", "Turkey",
  "UAE", "United Kingdom", "United States", "Vietnam",
];

export default function LevelForms() {
  useDocumentTitle("Level 2 · Forms — Practice");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef();
  const [range, setRange] = useState([25]);
  const [rangeDual, setRangeDual] = useState([20, 80]);
  const [auto, setAuto] = useState("");
  const [autoPick, setAutoPick] = useState("");
  const [showSug, setShowSug] = useState(false);

  // Debounced value used for the autocomplete suggestions.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(auto.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [auto]);
  const suggestions = useMemo(() => {
    if (debounced.length < 2) return [];
    return COUNTRIES.filter((c) => c.toLowerCase().includes(debounced)).slice(0, 6);
  }, [debounced]);

  // Simple form validation
  const [form, setForm] = useState({ name: "", email: "", website: "", pw: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (form.website && !/^https?:\/\//.test(form.website)) e.website = "Must start with http(s)://";
    if (form.pw.length < 8) e.pw = "Password must be at least 8 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const onSubmit = (ev) => {
    ev.preventDefault();
    if (validate()) setSubmitted(true);
  };

  return (
    <PracticeLayout
      eyebrow="Level 2 · Forms"
      title="Forms & form-level interactions"
      subtitle="Work through advanced form controls and full-form validation. All fields expose stable data-testids."
      seo={{
        title: "Level 2 · Forms — File Upload, Slider, Autocomplete",
        description: "Practise textarea, date picker, file upload, slider, debounced autocomplete and form-level validation for Selenium & Playwright.",
        keywords: "file upload automation practice, playwright autocomplete practice, slider automation, form validation testing",
        image: "/og/og-forms.svg",
      }}
    >
      <PracticeSection id="textarea" title="Textarea" hint="Practice fill(), maxLength assertion and typing speed.">
        <Textarea
          rows={4}
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Write your test notes… (max 200 chars)"
          data-testid="textarea-notes"
        />
        <div className="tag-mono text-[11px] text-zinc-500 mt-1 flex justify-between">
          <span data-testid="textarea-count">{note.length} / 200</span>
          <span data-testid="textarea-value" className="truncate max-w-[60%]">value: {note || "—"}</span>
        </div>
      </PracticeSection>

      <PracticeSection id="date" title="Date picker" hint="Native input[type=date] — accepts YYYY-MM-DD.">
        <div className="max-w-xs">
          <Label>Interview date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="input-date" className="mt-1.5" />
          <div data-testid="date-value" className="tag-mono text-[11px] text-zinc-500 mt-1">selected: {date || "—"}</div>
        </div>
      </PracticeSection>

      <PracticeSection id="file-upload" title="File upload" hint="Assert file.name & size after setInputFiles().">
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.pdf,.png,.jpg,.jpeg,.csv,.json"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="input-file"
          className="block text-sm"
        />
        {file && (
          <div className="mt-3 border border-zinc-200 rounded p-3 bg-zinc-50 text-xs tag-mono" data-testid="file-info">
            <div><b>name:</b> {file.name}</div>
            <div><b>size:</b> {file.size} bytes</div>
            <div><b>type:</b> {file.type || "unknown"}</div>
          </div>
        )}
      </PracticeSection>

      <PracticeSection id="slider" title="Slider" hint="Set value via keyboard: focus then ArrowRight/Left.">
        <div className="space-y-6 max-w-md">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Confidence</Label>
              <span data-testid="slider-single-value" className="tag-mono text-xs">{range[0]}</span>
            </div>
            <Slider value={range} onValueChange={setRange} max={100} step={1} data-testid="slider-single" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Range</Label>
              <span data-testid="slider-dual-value" className="tag-mono text-xs">{rangeDual[0]} – {rangeDual[1]}</span>
            </div>
            <Slider value={rangeDual} onValueChange={setRangeDual} max={100} step={1} data-testid="slider-dual" />
          </div>
        </div>
      </PracticeSection>

      <PracticeSection id="autocomplete" title="Autocomplete" hint="Debounced 250ms — remember to wait for the list.">
        <div className="relative max-w-sm">
          <Label>Country</Label>
          <Input
            value={auto}
            onChange={(e) => { setAuto(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
            placeholder="Type at least 2 chars…"
            data-testid="autocomplete-input"
            className="mt-1.5"
          />
          {showSug && suggestions.length > 0 && (
            <div
              data-testid="autocomplete-suggestions"
              className="absolute z-20 top-full mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg"
            >
              {suggestions.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => { setAuto(s); setAutoPick(s); setShowSug(false); }}
                  data-testid={`autocomplete-option-${s}`}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div data-testid="autocomplete-value" className="tag-mono text-[11px] text-zinc-500 mt-1">
            picked: {autoPick || "—"}
          </div>
        </div>
      </PracticeSection>

      <PracticeSection id="validation" title="Form validation" hint="Submit with empties to see errors — every error has its own testid.">
        <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-3 max-w-lg" noValidate>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="validate-name" className="mt-1.5" />
            {errors.name && <div data-testid="validate-name-error" className="text-xs text-red-600 mt-1">{errors.name}</div>}
          </div>
          <div>
            <Label>Email</Label>
            <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="validate-email" className="mt-1.5" />
            {errors.email && <div data-testid="validate-email-error" className="text-xs text-red-600 mt-1">{errors.email}</div>}
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} data-testid="validate-website" className="mt-1.5" placeholder="https://" />
            {errors.website && <div data-testid="validate-website-error" className="text-xs text-red-600 mt-1">{errors.website}</div>}
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={form.pw} onChange={(e) => setForm((f) => ({ ...f, pw: e.target.value }))} data-testid="validate-pw" className="mt-1.5" />
            {errors.pw && <div data-testid="validate-pw-error" className="text-xs text-red-600 mt-1">{errors.pw}</div>}
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Button type="submit" data-testid="validate-submit" className="bg-[#002FA7] hover:bg-[#00227A] text-white">Submit</Button>
            {submitted && Object.keys(errors).length === 0 && (
              <span data-testid="validate-success" className="text-sm text-emerald-700 tag-mono">✓ Form submitted successfully</span>
            )}
          </div>
        </form>
      </PracticeSection>
    </PracticeLayout>
  );
}
